// What a screen needs to know about the frame around it: whether the sidebar is
// a drawer now (compact) or folded to its icons (collapsed), how to open the
// full sidebar over the panel, the panel's width, and how much of its top-left
// corner window controls cover.
import { createContext, useContext } from "react";

export interface ShellFrame {
  compact: boolean;
  /**
   * Laid out for a phone with the phone's screens (shell/phone.tsx): the Dock
   * at the bottom has the ways to other screens and back to a list, so the
   * headers show neither the sidebar's button nor the way back.
   */
  phone: boolean;
  /** The frame has a folded sidebar to fold to. */
  foldable: boolean;
  /** The sidebar is in the row folded to its icons. */
  collapsed: boolean;
  /** Folded because the window is too narrow: unfolding means opening the drawer. */
  collapsedByWidth: boolean;
  /** Shows the full sidebar over the panel (compact or collapsed). */
  openDrawer(): void;
  /** Folds or unfolds the sidebar (kept on this device). */
  setCollapsed(collapsed: boolean): void;
  /** The panel's width (the window minus the rail and sidebar beside it). */
  panelWidth: number;
  /**
   * The folded sidebar's peek: `start` when the pointer comes onto its rows
   * (a mouse only; after a short rest the full sidebar shows over the panel),
   * `cancel` when it leaves before then.
   */
  peek: { start(pointerType: string): void; cancel(): void };
  /**
   * How far from its left edge a header row at the panel's top-left corner
   * starts, when window controls cover that corner (in the desktop app, when
   * compact or beside a folded sidebar; see title-bar.ts). 0 when nothing
   * covers it.
   */
  cornerInset: number;
  /**
   * The lock is laid over the end of the sidebar's first row (no window
   * controls' strip above to hold it): the row leaves that end free.
   */
  lockInRow: boolean;
  /**
   * A sidebar as wide as its content (the settings' sidebar): each copy of
   * it (the column, the drawer) says, under its own `key`, the width its
   * longest row needs, and null when it goes. While any is given, the sidebar
   * has the widest of them and dragging its edge only folds and unfolds it;
   * the kept width of Me's sidebar is left alone.
   */
  fitSidebar(key: object, width: number | null): void;
}

export const ShellFrameContext = createContext<ShellFrame>({
  compact: false,
  phone: false,
  foldable: false,
  collapsed: false,
  collapsedByWidth: false,
  openDrawer: () => {},
  setCollapsed: () => {},
  panelWidth: 1440,
  peek: { start: () => {}, cancel: () => {} },
  lockInRow: false,
  cornerInset: 0,
  fitSidebar: () => {},
});

/** The header rows' own left padding (`px-4`). */
const ROW_PADDING = 16;

/**
 * The style of a header row that sits at the panel's top-left corner. Never
 * less than the row's own padding.
 */
export const cornerStyle = (frame: ShellFrame) =>
  frame.cornerInset > 0
    ? { paddingLeft: Math.max(frame.cornerInset, ROW_PADDING) }
    : undefined;

export const useShellFrame = () => useContext(ShellFrameContext);
